import React from 'react';
import { Key, Zap, X } from 'lucide-react';

export interface ExtractedSymbol {
  name: string;
  abbreviation?: string;
  croppedImage?: string | null;
  box_2d?: number[] | null;
}

export interface KeyExtractionPreviewModalProps {
  show: boolean;
  extractedKeyPreview: ExtractedSymbol[];
  selectedKeyPreviewIndices: Set<number>;
  keyExtractionSnapshot: string | null;
  onToggleSelection: (idx: number) => void;
  onSelectAll: () => void;
  onClose: () => void;
  onCloseFull?: () => void;
  onAddToKeyOnly: () => void;
  onAddToKeyAndCount: () => void;
}

export const KeyExtractionPreviewModal: React.FC<KeyExtractionPreviewModalProps> = ({
  show,
  extractedKeyPreview,
  selectedKeyPreviewIndices,
  keyExtractionSnapshot,
  onToggleSelection,
  onSelectAll,
  onClose,
  onCloseFull,
  onAddToKeyOnly,
  onAddToKeyAndCount,
}) => {
  if (!show) return null;

  const selectedCount = extractedKeyPreview.filter((s, i) => selectedKeyPreviewIndices.has(i) && s.croppedImage).length;

  return (
    <div
      className="key-extraction-preview-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100002
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '16px',
          padding: '24px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid var(--border-color)'
        }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Key size={22} style={{ color: '#3b82f6' }} />
              Extracted Power Legend
            </h3>
            <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>
              Found {extractedKeyPreview.length} power components from the legend
              {selectedKeyPreviewIndices.size < extractedKeyPreview.length && (
                <span style={{ marginLeft: '8px', color: '#3b82f6', fontWeight: 500 }}>
                  ({selectedKeyPreviewIndices.size} selected)
                </span>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={onSelectAll}
              style={{
                background: 'transparent',
                border: '1px solid var(--border-strong)',
                borderRadius: '6px',
                color: '#3b82f6',
                cursor: 'pointer',
                padding: '4px 10px',
                fontSize: '12px',
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}
            >
              {selectedKeyPreviewIndices.size === extractedKeyPreview.length ? 'Deselect All' : 'Select All'}
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                padding: '8px'
              }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Snapshot Preview */}
        {keyExtractionSnapshot && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: 500 }}>
              Source Image:
            </p>
            <img
              src={keyExtractionSnapshot}
              alt="Key snapshot"
              style={{
                maxWidth: '100%',
                maxHeight: '120px',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                objectFit: 'contain'
              }}
            />
          </div>
        )}

        {/* Component Cards Grid */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '12px',
          padding: '4px'
        }}>
          {extractedKeyPreview.map((symbol, idx) => {
            const isSelected = selectedKeyPreviewIndices.has(idx);
            return (
              <div
                key={idx}
                onClick={() => onToggleSelection(idx)}
                style={{
                  background: 'var(--bg-secondary)',
                  borderRadius: '10px',
                  padding: '14px',
                  border: isSelected ? '2px solid #3b82f6' : '1px solid var(--border-color)',
                  transition: 'all 0.2s',
                  opacity: isSelected ? 1 : 0.4,
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (isSelected) e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.15)';
                }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  readOnly
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    accentColor: '#3b82f6'
                  }}
                />
                {symbol.croppedImage ? (
                  <div style={{
                    width: '100%',
                    height: '60px',
                    background: 'var(--bg-primary)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '10px',
                    border: '1px solid var(--border-color)',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={symbol.croppedImage}
                      alt={symbol.name}
                      style={{ maxWidth: '100%', maxHeight: '56px', objectFit: 'contain' }}
                    />
                  </div>
                ) : (
                  <div style={{
                    width: '100%',
                    height: '60px',
                    background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '10px',
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#6366f1'
                  }}>
                    {symbol.abbreviation || '◈'}
                  </div>
                )}
                <h4 style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', lineHeight: '1.3' }}>
                  {symbol.name}
                </h4>
                {symbol.abbreviation && (
                  <span style={{
                    display: 'inline-block',
                    background: 'var(--indigo-tint-medium)',
                    color: 'var(--blue-text-dark)',
                    fontSize: '10px',
                    fontWeight: 600,
                    padding: '2px 6px',
                    borderRadius: '4px',
                    marginBottom: '6px'
                  }}>
                    {symbol.abbreviation}
                  </span>
                )}
                {!symbol.croppedImage && (
                  <p style={{ margin: 0, fontSize: '10px', color: 'var(--text-placeholder)', fontStyle: 'italic' }}>
                    {symbol.box_2d ? 'Image crop failed' : 'No bounding box'}
                  </p>
                )}
              </div>
            );
          })}
          {extractedKeyPreview.length === 0 && (
            <div style={{ gridColumn: '1 / -1', padding: '40px', textAlign: 'center', color: 'var(--text-placeholder)' }}>
              <p style={{ fontSize: '16px', marginBottom: '8px' }}>No components extracted</p>
              <p style={{ fontSize: '13px' }}>Try selecting a clearer area of the legend</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div style={{
          marginTop: '20px',
          paddingTop: '16px',
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCloseFull ?? onClose}
            style={{
              padding: '10px 20px',
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-strong)',
              borderRadius: '8px',
              color: 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Close
          </button>
          <button
            onClick={onAddToKeyOnly}
            disabled={selectedCount === 0}
            style={{
              padding: '10px 20px',
              background: selectedCount > 0 ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
              border: selectedCount > 0 ? '2px solid #3b82f6' : '1px solid var(--border-strong)',
              borderRadius: '8px',
              color: selectedCount > 0 ? '#3b82f6' : 'var(--text-placeholder)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
              opacity: selectedCount > 0 ? 1 : 0.6,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Key size={16} />
            Add to Key Only ({selectedCount})
          </button>
          <button
            onClick={onAddToKeyAndCount}
            disabled={selectedCount === 0}
            style={{
              padding: '10px 20px',
              background: selectedCount > 0 ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'var(--border-strong)',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
              opacity: selectedCount > 0 ? 1 : 0.6,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Zap size={16} />
            Add to Key & Count ({selectedCount})
          </button>
        </div>
      </div>
    </div>
  );
};
